import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FilterPreset, FilterPresetsService } from 'src/app/api/filter-presets.service';
import { GlobalService } from 'src/app/api/global.service';

declare var bootstrap: any;

// Reusable "Saved Filters" dropdown button for a PrimeNG table's toolbar - lets a firm's team save
// their current column-filter state (table.filters) as a named preset and reapply it later. The host
// table's own component owns the actual Table instance(s) (grouped views can have more than one), so
// this stays generic: getCurrentFilters reads the filters to save, applyFilters hands a loaded preset's
// filters back for the host to apply to its own table(s) and refresh its own "active filters" display.
@Component({
  selector: 'app-filter-presets',
  templateUrl: './filter-presets.component.html',
  styleUrls: ['./filter-presets.component.scss'],
})
export class FilterPresetsComponent implements OnInit {
  @Input() table_key: string;
  @Input() hasActiveFilters = false;
  @Input() getCurrentFilters: () => any;
  // Field key -> plain-English label (e.g. projectFilterLabelMap/taskFilterLabelMap from the host
  // table component) - lets DescribeFilters() render a saved filter's hover tooltip using the same
  // labels the host's own "Column filters active" indicator uses, without this component needing to
  // know anything about the host table's columns itself.
  @Input() fieldLabelMap: { [field: string]: string } = {};
  // Optional: lets a host that saves more than just PrimeNG column filters (e.g. the projects table's
  // "above the table" toggle buttons - status, My Role Filter, Group By, etc., bundled alongside the
  // column filters as { columnFilters, toolbar }) describe that extra "toolbar" state in the hover
  // tooltip too. Returns plain-English lines to append after the column-filter description; omitted
  // entirely for a host that only ever saves bare column filters.
  @Input() describeToolbarState?: (toolbar: any) => string[];
  @Output() applyFilters = new EventEmitter<any>();

  @ViewChild('toggleButton') toggleButton: ElementRef;

  presets: FilterPreset[] = [];
  loaded = false;
  showSaveInput = false;
  newPresetName = '';
  // Only meaningful (and only shown) for admins - non-admins can only ever create personal presets.
  // Defaults to personal so saving a filter never silently becomes everyone's default without the
  // admin actively choosing that.
  newPresetScope: 'personal' | 'firm_wide' = 'personal';
  saving = false;
  presetSearchTerm = '';

  private static readonly MATCH_MODE_TEXT: { [mode: string]: string } = {
    contains: 'contains', notContains: 'does not contain', equals: 'is', notEquals: 'is not',
    startsWith: 'starts with', endsWith: 'ends with', in: 'is',
    lt: 'is less than', lte: 'is at most', gt: 'is greater than', gte: 'is at least',
    is: 'is', isNot: 'is not', before: 'is before', after: 'is after',
    dateIs: 'is', dateIsNot: 'is not', dateBefore: 'is before', dateAfter: 'is after',
    singleIncludesAny: 'includes any of', singleIncludesAll: 'includes all of',
    includesAny: 'includes any of', includesAll: 'includes all of', includesNone: 'excludes',
  };

  constructor(private filterPresetsService: FilterPresetsService, public globalService: GlobalService) { }

  ngOnInit() {
    this.LoadPresets();
  }

  get isAdmin(): boolean {
    return !!this.globalService.GetCurrentUser()?.is_admin;
  }

  // Search box + A-Z sort applied together, independent of the order presets happen to be stored in
  // (load order, insertion order after a save, etc.) - guarantees each category is always alphabetical
  // and always reflects the current search, rather than relying on the backing array staying sorted.
  get firmWidePresets(): FilterPreset[] {
    return this.FilterAndSort(this.presets.filter(p => !!p.is_firm_wide));
  }

  get personalPresets(): FilterPreset[] {
    return this.FilterAndSort(this.presets.filter(p => !p.is_firm_wide));
  }

  get hasAnyPresets(): boolean {
    return this.presets.length > 0;
  }

  get hasVisiblePresets(): boolean {
    return this.firmWidePresets.length > 0 || this.personalPresets.length > 0;
  }

  private FilterAndSort(list: FilterPreset[]): FilterPreset[] {
    const query = this.presetSearchTerm?.trim().toLowerCase();
    const filtered = query ? list.filter(p => p.title?.toLowerCase().includes(query)) : list;
    return [...filtered].sort((a, b) => a.title.localeCompare(b.title));
  }

  async LoadPresets() {
    if (!this.table_key) return;
    this.presets = await this.filterPresetsService.List(this.table_key);
    this.loaded = true;
  }

  ToggleSaveInput() {
    this.showSaveInput = !this.showSaveInput;
    this.newPresetName = '';
    this.newPresetScope = 'personal';
  }

  async SaveCurrentFilter() {
    const name = this.newPresetName?.trim();
    const filters = this.getCurrentFilters?.();
    if (!name || !filters || this.saving) return;
    this.saving = true;
    const is_firm_wide = this.isAdmin && this.newPresetScope === 'firm_wide';
    const created = await this.filterPresetsService.Save(this.table_key, name, filters, is_firm_wide);
    this.saving = false;
    if (created) {
      this.presets.push(created);
      this.showSaveInput = false;
      this.newPresetName = '';
      this.newPresetScope = 'personal';
      this.globalService.presentToastMessage(`Saved filter "${created.title}"`, true);
    } else {
      this.globalService.presentToastMessage('Could not save filter preset', false);
    }
  }

  ApplyPreset(preset: FilterPreset) {
    let parsed: any = null;
    try { parsed = JSON.parse(preset.filters_json); } catch { parsed = null; }
    if (!parsed) return;
    this.applyFilters.emit(parsed);
    this.CloseDropdown();
  }

  // Mirrors FilterPresets.cs's delete authorization: a firm-wide preset can only be removed by an
  // admin (even by whoever created it), a personal preset by its own creator or an admin.
  CanDeletePreset(preset: FilterPreset): boolean {
    const user = this.globalService.GetCurrentUser();
    if (user?.is_admin) return true;
    if (preset.is_firm_wide) return false;
    return !!preset.created_by_guid && preset.created_by_guid === user?.guid;
  }

  async DeletePreset(preset: FilterPreset, event: Event) {
    event.stopPropagation();
    if (!window.confirm(`Delete the saved filter "${preset.title}"?`)) return;
    const result = await this.filterPresetsService.Delete(this.table_key, preset.guid);
    if (result.success) {
      this.presets = this.presets.filter(p => p.guid !== preset.guid);
    } else {
      this.globalService.presentToastMessage(result.message || 'Could not delete filter preset', false);
    }
  }

  // Plain-English hover tooltip for a saved filter - decodes PrimeNG's filters_json (per-field
  // {value, matchMode} or an array of those for multi-condition column filters) into e.g. "Title
  // contains "abc"<br>Status is "Active" or "Watch"". Falls back to the raw field key when
  // fieldLabelMap doesn't know it, so this still degrades gracefully for filters saved before a field
  // was added to the map, or for a host that didn't pass one. Handles both the plain PrimeNG-filters
  // shape and a host's wrapped { columnFilters, toolbar } shape (see describeToolbarState).
  DescribeFilters(preset: FilterPreset): string {
    let parsed: any;
    try { parsed = JSON.parse(preset.filters_json); } catch { return 'Unable to describe this filter.'; }
    if (!parsed || typeof parsed !== 'object') return 'No filters set.';

    const isWrapped = ('columnFilters' in parsed) || ('toolbar' in parsed);
    const columnFilters = isWrapped ? parsed.columnFilters : parsed;
    const toolbar = isWrapped ? parsed.toolbar : null;

    const parts = this.DescribeColumnFilters(columnFilters);
    if (toolbar && this.describeToolbarState) {
      parts.push(...(this.describeToolbarState(toolbar) || []));
    }
    return parts.length ? parts.join('<br>') : 'No filters set.';
  }

  private DescribeColumnFilters(parsed: any): string[] {
    if (!parsed || typeof parsed !== 'object') return [];
    const parts: string[] = [];
    for (const field of Object.keys(parsed)) {
      const raw = parsed[field];
      const constraints = Array.isArray(raw) ? raw : [raw];
      const descriptions = constraints.map((c: any) => this.DescribeConstraint(c)).filter((d: string) => !!d);
      if (!descriptions.length) continue;
      const label = this.fieldLabelMap?.[field] || field;
      parts.push(`<strong>${label}</strong> ${descriptions.join(' or ')}`);
    }
    return parts;
  }

  private DescribeConstraint(c: any): string {
    if (!c || c.value === null || c.value === undefined || c.value === '') return '';
    const verb = FilterPresetsComponent.MATCH_MODE_TEXT[c.matchMode] || 'is';
    if (Array.isArray(c.value)) {
      if (!c.value.length) return '';
      return `${verb} ${c.value.join(', ')}`;
    }
    return `${verb} "${c.value}"`;
  }

  private CloseDropdown() {
    try {
      const el = this.toggleButton?.nativeElement;
      if (el) bootstrap.Dropdown.getOrCreateInstance(el).hide();
    } catch { }
  }
}
